import { useChatWorkflowShared } from "@intelligems/ig-plugin/builder";
import { UiStreamAction } from "@intelligems/ig-types";
import { useEffect, useRef } from "react";

import { aiApiClient } from "@src/api/ai-api";
import { useDispatch, useSelector } from "@src/store";
import { clearAiChat, getAiChat } from "@src/store/slices/aiChat";
import { ExperienceBuilderSelector } from "@src/store/slices/experienceBuilder";
import {
  ExperienceBuilderAiAction,
  ExperienceBuilderAiSelector,
  vivStreamAction,
} from "@src/store/slices/experienceBuilder/ai";
import {
  clearVivChatWorkflowHandle,
  setVivChatWorkflowHandle,
} from "@src/store/slices/experienceBuilder/ai/vivChatWorkflowHandle";

import { logToolActivity } from "@src/features/ai/components/AiChat/utils/logToolActivity";
import { useStopChat } from "@src/features/ai/components/AiChat/hooks/useStopChat";

/**
 * The one React-bound piece of the Viv chat. It creates the streaming-workflow
 * hook, dispatches each stream message as a `vivStreamAction`, and registers
 * the hook's `mutate`/`stop` as the `VivChatWorkflowHandle` so the Redux thunks
 * (`fireVivTurn`, `stopVivChat`) can drive turns. All turn logic lives in
 * Redux; this hook only bridges to the parts that must exist in a component.
 *
 * Mount it once at the dialog level so the stream survives the chat panel being
 * minimized.
 */
export const useVivChatBridge = () => {
  const dispatch = useDispatch();
  const sessionId = useSelector((state) => getAiChat(state).sessionId);

  const onStreamAction = (data: UiStreamAction) => {
    logToolActivity(data);
    dispatch(vivStreamAction({ data }));
  };

  const chatWorkflow = useChatWorkflowShared(aiApiClient, onStreamAction);
  const stop = useStopChat(chatWorkflow, sessionId);

  // Register the latest mutate/stop for the thunks; clear on unmount so a
  // continuation can never fire against a torn-down hook.
  useEffect(() => {
    setVivChatWorkflowHandle({ mutate: chatWorkflow.mutate, stop });
    return () => clearVivChatWorkflowHandle();
  }, [chatWorkflow.mutate, stop]);

  // Hand-off from the experience-creation chat: fire Viv's first turn with the
  // merchant's actual last message, instead of leaving the chat empty and
  // making them retype the request. Declared as a SECOND effect in this same
  // hook (not in `VivChat`) so it's guaranteed to run after the handle
  // registration above — effect order within one component follows
  // declaration order, whereas a separate effect in the child `VivChat`
  // component would fire before this one (React runs descendant effects
  // before their parent's), racing `fireVivTurn` against an unregistered
  // handle and silently no-op'ing it. Gated on `isPageReady` since the preview
  // iframe is still navigating to its target URL when the builder first opens.
  //
  // Deliberately NOT gated on `getIsNewSession` (`turns.length === 0`): a
  // trailing stream event from the creation agent's own concluding step (its
  // network connection, not Viv's) can land in this now-shared `aiChat` state
  // just after the hand-off clears it — creating an empty turn/node structure
  // with no visible message. That alone doesn't just block a naive gate here —
  // `prepareChatMutation` runs its OWN `getIsNewSession` check to decide
  // whether to send a `"start"` request (workflowId + initialContext, for a
  // session the server has never seen) or a `"continue"` request (assumes the
  // server already has a workflow running for that session). With the stray
  // turn still present, it wrongly picks `"continue"` for a session the
  // backend has genuinely never seen — the server has nothing to continue, and
  // silently no-ops (the HTTP call still resolves cleanly with zero content,
  // which is why this looked like a client-side failure at first). Re-clearing
  // immediately before firing — synchronously, right here — closes that leak
  // window to effectively zero, so `prepareChatMutation` sees a genuinely
  // empty transcript and sends the correct `"start"` request.
  const creationBriefLastMessage = useSelector(
    ExperienceBuilderAiSelector.getCreationBriefLastMessage
  );
  const isPageReady =
    useSelector(ExperienceBuilderSelector.getPageState)?.type === "ready";
  const hasAutoStartedFromBrief = useRef(false);
  useEffect(() => {
    if (!isPageReady || hasAutoStartedFromBrief.current) return;
    if (!creationBriefLastMessage) return;

    hasAutoStartedFromBrief.current = true;
    dispatch(clearAiChat());
    void dispatch(
      ExperienceBuilderAiAction.fireVivTurn({
        kind: "user-first",
        input: { type: "text", content: creationBriefLastMessage },
      })
    );
  }, [isPageReady, creationBriefLastMessage, dispatch]);
};
